import React, { useEffect, useState } from "react";
import {
  getAllTeam,
  createTeam,
  updateTeam,
  deleteTeam,
} from "../apis/teamApis";

const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);

  const [teamName, setTeamName] = useState("");
  const [fieldInput, setFieldInput] = useState("");
  const [fields, setFields] = useState([]);

  /* ---------- FETCH TEAMS ---------- */
  const fetchTeams = async () => {
    try {
      const res = await getAllTeam();
      setTeams(res.data || res);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeams();
  }, []);

  /* ---------- OPEN CREATE ---------- */
  const openCreate = () => {
    setEditingTeam(null);
    setTeamName("");
    setFields([]);
    setShowModal(true);
  };

  /* ---------- OPEN EDIT ---------- */
  const openEdit = (team) => {
    setEditingTeam(team);
    setTeamName(team.name);
    setFields(team.fields || []);
    setShowModal(true);
  };

  /* ---------- ADD FIELD ---------- */
  const addField = () => {
    if (!fieldInput.trim()) return;
    setFields([...fields, fieldInput.trim()]);
    setFieldInput("");
  };

  /* ---------- REMOVE FIELD ---------- */
  const removeField = (index) => {
    setFields(fields.filter((_, i) => i !== index));
  };

  /* ---------- SAVE TEAM ---------- */
  const saveTeam = async () => {
    if (!teamName.trim()) return;

    const payload = {
      name: teamName,
      fields,
    };

    try {
      if (editingTeam) {
        await updateTeam(editingTeam._id, payload);
      } else {
        await createTeam(payload);
      }

      fetchTeams();
      setShowModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  /* ---------- DELETE TEAM ---------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this team?")) return;

    try {
      await deleteTeam(id);
      fetchTeams();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return <div className="p-10 text-center">Loading teams...</div>;
  }

  return (
    <div className="min-h-screen p-4">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Teams</h1>
        <button
          onClick={openCreate}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700"
        >
          + Create Team
        </button>
      </div>

      {/* CARD VIEW */}
      {teams.length === 0 ? (
        <p className="text-gray-500 text-center mt-20">
          No teams found
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {teams.map((team) => (
            <div
              key={team._id}
              className="bg-white rounded-xl shadow-md p-5 flex flex-col justify-between"
            >
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  {team.name}
                </h3>

                <div className="flex flex-wrap gap-2 mt-3">
                  {team.fields?.length > 0 ? (
                    team.fields.map((field, index) => (
                      <span
                        key={index}
                        className="bg-gray-200 text-sm px-3 py-1 rounded-full"
                      >
                        {field}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">
                      No fields added
                    </span>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => openEdit(team)}
                  className="px-3 py-1 border rounded-lg hover:bg-gray-100"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(team._id)}
                  className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-md rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold mb-4">
              {editingTeam ? "Edit Team" : "Create Team"}
            </h2>

            <div className="space-y-4">
              {/* TEAM NAME */}
              <input
                type="text"
                placeholder="Team name"
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                className="w-full border rounded-lg px-4 py-2"
              />

              {/* FIELD INPUT */}
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add field"
                  value={fieldInput}
                  onChange={(e) => setFieldInput(e.target.value)}
                  className="flex-1 border rounded-lg px-4 py-2"
                />
                <button
                  onClick={addField}
                  className="bg-red-600 text-white px-4 rounded-lg"
                >
                  Add
                </button>
              </div>

              {/* FIELD CHIPS */}
              <div className="flex flex-wrap gap-2">
                {fields.map((field, index) => (
                  <span
                    key={index}
                    className="bg-gray-200 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {field}
                    <button
                      onClick={() => removeField(index)}
                      className="text-red-600 font-bold"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>

              {/* ACTIONS */}
              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={saveTeam}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;
